import { FCElement } from '@fc/component';
import { collectCartAddSectionIds, submitCartAdd } from '@fc/cart';
import {
  CartSectionsRenderedEvent,
  FCEvents,
  createCartAddOperationId,
  emitCartAddFailure,
  emitCartAddPending,
  emitCartAddPendingClear,
  emitCartAddSuccess,
  getCartEventData,
  isCartAddSuccessEvent,
} from '@fc/events';
import { cartPerformance } from '@fc/metrics';
import { sectionManager } from '@fc/sections';

const QUICK_ADD_MODAL_SECTION_ID = 'quick-add-modal-product';
const QUICK_ADD_MODAL_BODY_SELECTOR = '[data-quick-add-modal-body]';
const QUICK_ADD_ERROR_DISPLAY_MS = 7000;
const QUICK_ADD_CART_DRAWER_RETURN_TIMEOUT_MS = 1600;
const MARKET_CONTEXT_QUERY_KEYS = ['country', 'locale'];

function preserveMarketContext(url) {
  const current = new URL(window.location.href);

  for (const key of MARKET_CONTEXT_QUERY_KEYS) {
    const value = current.searchParams.get(key);
    if (value && !url.searchParams.has(key)) {
      url.searchParams.set(key, value);
    }
  }

  return url;
}

function parsePositiveInt(value) {
  const number = Number.parseInt(String(value ?? ''), 10);
  return Number.isFinite(number) && number > 0 ? number : 0;
}

export class QuickAddDialogComponent extends FCElement {
  requiredRefs = ['dialogComponent'];
  #requestController = null;
  #activeTrigger = null;
  #cartDrawerReturnTarget = null;
  #pendingCartDrawerReturn = null;
  #cartDrawerReturnTimer = null;

  connectedCallback() {
    super.connectedCallback();
    this.addManagedEventListener(this, FCEvents.variantChange, this.#onVariantChange);
    this.addManagedEventListener(this, FCEvents.cartRefresh, this.#onModalCartRefresh);
    this.addManagedEventListener(document, CartSectionsRenderedEvent.eventName, this.#onCartSectionsRendered);
    this.addManagedEventListener(this, 'dialog:close', this.#onDialogClose);
  }

  disconnectedCallback() {
    this.#abortRequest();
    this.#clearCartDrawerReturn();
    super.disconnectedCallback();
  }

  openProduct = async ({ productUrl, productTitle = '', optionValueIds = [], trigger = null } = {}) => {
    const dialogComponent = this.refs.dialogComponent;
    const content = this.#contentElement();
    if (!dialogComponent || !(content instanceof HTMLElement) || !productUrl) return;

    this.#abortRequest();
    this.#setActiveTrigger(trigger);
    this.#requestController = new AbortController();
    const { signal } = this.#requestController;

    dialogComponent.showDialog({ reason: 'quick-add', productUrl });
    this.#resetAccessibleName();
    this.#renderLoading(productTitle);

    try {
      const url = this.#buildProductUrl(productUrl, optionValueIds);
      const doc = await sectionManager.getSectionDocument(QUICK_ADD_MODAL_SECTION_ID, {
        cache: false,
        signal,
        url,
      });

      if (signal.aborted) return;

      this.#replaceContent(doc, productUrl);
    } catch (error) {
      if (signal.aborted) return;
      this.#renderError(productUrl);
    } finally {
      if (this.#requestController?.signal === signal) {
        this.#requestController = null;
      }
    }
  };

  #buildProductUrl(productUrl, optionValueIds = []) {
    const url = preserveMarketContext(new URL(productUrl, window.location.origin));
    url.searchParams.delete('variant');

    const ids = Array.isArray(optionValueIds) ? optionValueIds.filter(Boolean) : [];
    if (ids.length) {
      url.searchParams.set('option_values', ids.join(','));
    } else {
      url.searchParams.delete('option_values');
    }

    return url;
  }

  #replaceContent(doc, productUrl) {
    const content = this.#contentElement();
    if (!(content instanceof HTMLElement)) return;

    const sectionRoot = doc?.getElementById?.(`shopify-section-${QUICK_ADD_MODAL_SECTION_ID}`) ?? doc;
    const nextBody = sectionRoot?.querySelector?.(QUICK_ADD_MODAL_BODY_SELECTOR);
    if (!(nextBody instanceof HTMLElement)) {
      this.#renderError(productUrl);
      return;
    }

    content.replaceChildren(document.importNode(nextBody, true));
    this.#syncAccessibleName();
    this.#syncVariantState();

    requestAnimationFrame(() => {
      const title = this.querySelector('[data-quick-add-modal-title]');
      if (title instanceof HTMLElement) {
        title.focus({ preventScroll: true });
      }
    });
  }

  #syncAccessibleName() {
    const dialog = this.refs.dialogComponent?.refs?.dialog;
    const title = this.querySelector('[data-quick-add-modal-title]');
    if (!(dialog instanceof HTMLDialogElement) || !(title instanceof HTMLElement) || !title.id) return;

    dialog.setAttribute('aria-labelledby', title.id);
    dialog.removeAttribute('aria-label');
  }

  #resetAccessibleName() {
    const dialog = this.refs.dialogComponent?.refs?.dialog;
    if (!(dialog instanceof HTMLDialogElement)) return;

    dialog.removeAttribute('aria-labelledby');
    dialog.setAttribute('aria-label', dialog.dataset.defaultLabel || 'Quick add');
  }

  #renderLoading(productTitle = '') {
    const content = this.#contentElement();
    if (!(content instanceof HTMLElement)) return;

    const loading = document.createElement('div');
    loading.className = 'quick-add-modal__loading';
    loading.setAttribute('role', 'status');
    loading.setAttribute('aria-live', 'polite');
    loading.textContent = window.FitCheck?.translations?.loading || 'Loading...';
    if (productTitle) {
      loading.setAttribute('aria-label', `${loading.textContent} ${productTitle}`);
    }

    content.replaceChildren(loading);
  }

  #renderError(productUrl = '') {
    const content = this.#contentElement();
    if (!(content instanceof HTMLElement)) return;

    const error = document.createElement('div');
    error.className = 'quick-add-modal__error';
    error.setAttribute('role', 'alert');

    const message = document.createElement('p');
    message.textContent = window.FitCheck?.translations?.quick_add_unavailable || 'Unable to load purchase options.';
    error.append(message);

    if (productUrl) {
      const link = document.createElement('a');
      link.href = new URL(productUrl, window.location.origin).href;
      link.textContent = window.FitCheck?.translations?.view_more_details || 'More details';
      error.append(link);
    }

    content.replaceChildren(error);
  }

  #contentElement() {
    return this.querySelector('#quick-add-modal-content');
  }

  #setActiveTrigger(trigger) {
    if (this.#activeTrigger instanceof HTMLElement && this.#activeTrigger !== trigger) {
      this.#activeTrigger.setAttribute('aria-expanded', 'false');
    }

    this.#activeTrigger = trigger instanceof HTMLElement ? trigger : null;
    this.#cartDrawerReturnTarget = this.#resolveCartDrawerReturnTarget(this.#activeTrigger);
    this.#activeTrigger?.setAttribute('aria-expanded', 'true');
  }

  #resolveCartDrawerReturnTarget(trigger) {
    if (!(trigger instanceof HTMLElement)) return null;
    if (!trigger.closest('.cart-drawer-upsells')) return null;

    const drawer = trigger.closest('cart-drawer');
    return drawer instanceof HTMLElement ? drawer : null;
  }

  #onVariantChange = (event) => {
    this.#syncVariantState(event.detail?.resource ?? null);
  };

  #onModalCartRefresh = (event) => {
    if (!this.#cartDrawerReturnTarget) return;
    if (!isCartAddSuccessEvent(event)) return;

    const data = getCartEventData(event);
    this.#pendingCartDrawerReturn = {
      operationId: data.cartAddOperationId || null,
      sourceId: event.detail?.sourceId || null,
    };
    this.#scheduleCartDrawerReturnFallback();
  };

  #onCartSectionsRendered = (event) => {
    if (!this.#pendingCartDrawerReturn) return;
    if (!this.#matchesCartDrawerReturn(event)) return;

    void this.#finishCartDrawerReturn('cart-sections-rendered');
  };

  #matchesCartDrawerReturn(event) {
    const pending = this.#pendingCartDrawerReturn;
    if (!pending) return false;

    const data = getCartEventData(event);
    if (pending.operationId && data.cartAddOperationId) {
      return pending.operationId === data.cartAddOperationId;
    }

    const sourceId = event.detail?.sourceId || null;
    return !pending.sourceId || !sourceId || pending.sourceId === sourceId;
  }

  #scheduleCartDrawerReturnFallback() {
    window.clearTimeout(this.#cartDrawerReturnTimer);
    this.#cartDrawerReturnTimer = window.setTimeout(() => {
      this.#cartDrawerReturnTimer = null;
      if (this.#pendingCartDrawerReturn) {
        void this.#finishCartDrawerReturn('cart-sections-timeout');
      }
    }, QUICK_ADD_CART_DRAWER_RETURN_TIMEOUT_MS);
  }

  async #finishCartDrawerReturn(reason) {
    const drawer = this.#cartDrawerReturnTarget;
    this.#clearCartDrawerReturn({ keepTarget: true });
    if (!(drawer instanceof HTMLElement) || !drawer.isConnected) return;

    const dialogComponent = this.refs.dialogComponent;
    if (dialogComponent?.refs?.dialog?.open && typeof dialogComponent.closeDialog === 'function') {
      await dialogComponent.closeDialog({ restoreFocus: false, reason: `quick-add-${reason}` });
    }

    this.#openCartDrawerReturn(drawer);
    this.#cartDrawerReturnTarget = null;
  }

  #openCartDrawerReturn(drawer) {
    requestAnimationFrame(() => {
      if (typeof drawer.open === 'function') {
        drawer.open();
      } else if (typeof drawer.showDialog === 'function') {
        drawer.showDialog({ reason: 'quick-add-return' });
      }

      requestAnimationFrame(() => {
        const focusTarget = drawer.querySelector('[data-dialog-initial-focus]');
        if (focusTarget instanceof HTMLElement) {
          focusTarget.focus({ preventScroll: true });
        }
      });
    });
  }

  #clearCartDrawerReturn({ keepTarget = false } = {}) {
    window.clearTimeout(this.#cartDrawerReturnTimer);
    this.#cartDrawerReturnTimer = null;
    this.#pendingCartDrawerReturn = null;
    if (!keepTarget) {
      this.#cartDrawerReturnTarget = null;
    }
  }

  #syncVariantState(resource) {
    const body = this.querySelector(QUICK_ADD_MODAL_BODY_SELECTOR);
    if (!(body instanceof HTMLElement)) return;

    const state = this.#resolveVariantState(body, resource);
    body.dataset.selectedVariantState = state;
    if (resource?.id) {
      body.dataset.selectedVariantId = String(resource.id);
    } else if (state !== 'selected') {
      body.dataset.selectedVariantId = '';
    }

    const status = body.querySelector('[data-quick-add-selection-status]');
    if (status instanceof HTMLElement) {
      const label = this.#statusLabel(body, state);
      status.textContent = label;
      status.toggleAttribute('hidden', !label);
    }

    this.#setPurchaseDisabled(state !== 'selected');
  }

  #resolveVariantState(body, resource) {
    if (resource?.available === true) return 'selected';
    if (resource?.available === false) return 'sold_out';
    if (resource === undefined) return body.dataset.selectedVariantState || 'unresolved';

    const picker = body.querySelector('variant-select');
    const optionCount = parsePositiveInt(body.dataset.optionCount);
    const selectedOptionCount =
      Array.isArray(picker?.selectedOptionsValues) && picker.selectedOptionsValues.length
        ? picker.selectedOptionsValues.length
        : parsePositiveInt(body.dataset.selectedOptionCount);

    if (optionCount > 0 && selectedOptionCount < optionCount) return 'unresolved';
    if (resource === null) return 'nonexistent';

    return body.dataset.selectedVariantState || 'unresolved';
  }

  #statusLabel(body, state) {
    if (state === 'selected') return '';
    if (state === 'sold_out') return body.dataset.fitSoldOutLabel || 'Out of stock';
    if (state === 'unresolved') return body.dataset.fitUnresolvedLabel || 'Choose available options';
    return body.dataset.fitUnavailableLabel || 'Unavailable';
  }

  #setPurchaseDisabled(disabled) {
    if (!disabled) return;

    this.querySelectorAll('[data-ref="addToCartButton"], qty-selector button, qty-selector input').forEach((control) => {
      if (control instanceof HTMLButtonElement || control instanceof HTMLInputElement) {
        control.disabled = true;
        control.setAttribute('aria-disabled', 'true');
      }
    });

    this.querySelectorAll('input[name="id"]').forEach((input) => {
      if (input instanceof HTMLInputElement) input.value = '';
    });
  }

  #onDialogClose = () => {
    this.#abortRequest();
    if (this.#pendingCartDrawerReturn) {
      this.#clearCartDrawerReturn();
    }
    this.#activeTrigger?.setAttribute('aria-expanded', 'false');
    this.#activeTrigger = null;
  };

  #abortRequest() {
    this.#requestController?.abort();
    this.#requestController = null;
  }
}

export class QuickAddComponent extends FCElement {
  #errorTimer = null;

  connectedCallback() {
    super.connectedCallback();

    this.addManagedEventListener(document, 'click', this.#onOutsideClick, { capture: true });
    this.addManagedEventListener(this, 'click', this.#onPlusClick, { capture: true });
    this.addManagedEventListener(this, 'click', this.#onSizeClick, { capture: true });
    this.addManagedEventListener(this, 'keydown', this.#onKeyDown);
    this.addManagedEventListener(this, 'submit', this.#onSubmit, { capture: true });
    this.addManagedEventListener(this.closest('product-tile, product-card'), FCEvents.variantChange, this.#onVariantChange);

    if (this.dataset.hoverTrigger === 'true') {
      const plus = this.querySelector('.quick-add__button--plus');
      if (plus) {
        this.addManagedEventListener(plus, 'mouseenter', this.#onHoverIn);
        this.addManagedEventListener(this, 'mouseleave', this.#onHoverOut);
      }
    }
  }

  disconnectedCallback() {
    this.#clearError({ remove: true });
    super.disconnectedCallback();
  }

  #selectedCardOptionState(button = null) {
    const card = this.closest('product-tile, product-card');
    const selectedValuesByPosition = new Map();
    let productUrl = this.dataset.productUrl || '';
    if (!card) return { selectedValuesByPosition, productUrl };

    card.querySelectorAll('input[type="radio"]:checked[data-option-value-id]').forEach((radio) => {
      if (!(radio instanceof HTMLInputElement)) return;
      if (radio.closest('quick-add') === this) return;

      const position = this.#optionPosition(radio);
      if (!position) return;

      const optionValueId = radio.dataset.optionValueId;
      if (!optionValueId) return;

      selectedValuesByPosition.set(position, optionValueId);
      productUrl = radio.dataset.connectedProductUrl || radio.dataset.productUrl || productUrl;
    });

    if (button instanceof HTMLElement) {
      const buttonPosition = this.#optionPosition(button);
      const buttonOptionValueId = button.dataset.optionValueId;

      if (buttonPosition && buttonOptionValueId) {
        selectedValuesByPosition.set(buttonPosition, buttonOptionValueId);
      }

      productUrl = button.dataset.connectedProductUrl || button.dataset.productUrl || productUrl || '';
    }

    return { selectedValuesByPosition, productUrl };
  }

  #optionCount() {
    const declared = parseInt(this.dataset.optionCount || '0', 10);
    if (declared > 0) return Math.min(declared, 3);

    return 0;
  }

  #optionPosition(element) {
    const raw =
      element?.dataset?.fitOptionPosition ||
      element?.dataset?.inputId?.split('-')?.[0] ||
      element?.dataset?.pickerOptionPosition ||
      this.dataset.pickerOptionPosition ||
      this.dataset.sizeOptionPosition ||
      '';
    const position = parseInt(raw, 10);
    return Number.isFinite(position) && position > 0 ? position : null;
  }

  #selectedOptionValueIds(button = null) {
    const { selectedValuesByPosition } = this.#selectedCardOptionState(button);

    return Array.from(selectedValuesByPosition.entries())
      .sort(([positionA], [positionB]) => positionA - positionB)
      .map(([, optionValueId]) => optionValueId)
      .filter(Boolean);
  }

  #hasCompleteOptionSelection(button = null) {
    const optionCount = this.#optionCount();
    if (optionCount <= 0) return false;

    const { selectedValuesByPosition } = this.#selectedCardOptionState(button);
    return selectedValuesByPosition.size >= optionCount;
  }

  #buttonCanResolve(button) {
    if (!(button instanceof HTMLElement)) return false;
    return this.#hasCompleteOptionSelection(button);
  }

  #sizePanel = () => this.querySelector('.quick-add__size-selector');
  #plusButton = () => this.querySelector('.quick-add__button--plus');
  #usesModal = () => this.dataset.quickAddMode === 'modal';

  #refreshAvailability() {
    const panel = this.#sizePanel();
    if (!panel) return;

    panel.querySelectorAll('.quick-add__size-button').forEach((btn) => {
      const optionValue = btn.dataset.optionValue || btn.dataset.sizeValue;
      if (!optionValue) return;
      const available = this.#buttonCanResolve(btn);
      btn.classList.toggle('quick-add__size-button--unavailable', !available);
      btn.classList.toggle('fit-quick-add__size-button--unavailable', !available);
      btn.setAttribute('aria-disabled', available ? 'false' : 'true');
      if (available) {
        btn.removeAttribute('disabled');
      } else {
        btn.setAttribute('disabled', '');
        btn.classList.remove('quick-add__size-button--selected');
      }
    });
  }

  #showPanel({ focusPanel = false } = {}) {
    const panel = this.#sizePanel();
    if (!panel) return;
    this.#refreshAvailability();

    const hasResolvableButton = panel.querySelector('.quick-add__size-button:not(:disabled)');
    if (!hasResolvableButton) {
      this.#openModal(this.#plusButton());
      return;
    }

    panel.removeAttribute('hidden');
    panel.removeAttribute('inert');
    panel.setAttribute('aria-hidden', 'false');
    this.#plusButton()?.setAttribute('aria-expanded', 'true');

    if (focusPanel) {
      const firstAvailableButton = panel.querySelector('.quick-add__size-button:not(:disabled)');
      firstAvailableButton?.focus();
    }
  }

  #hidePanel({ returnFocus = false } = {}) {
    const panel = this.#sizePanel();
    if (!panel) return;

    const focusWasInsidePanel = panel.contains(document.activeElement);
    panel.setAttribute('hidden', '');
    panel.setAttribute('inert', '');
    panel.setAttribute('aria-hidden', 'true');
    this.#plusButton()?.setAttribute('aria-expanded', 'false');

    if (returnFocus || focusWasInsidePanel) {
      this.#plusButton()?.focus();
    }
  }

  #onHoverIn = () => this.#showPanel();

  #onHoverOut = () => this.#hidePanel();

  #onPlusClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const modalTrigger = target?.closest('[data-quick-add-modal-trigger]');
    if (modalTrigger && this.contains(modalTrigger)) {
      event.stopImmediatePropagation();
      event.stopPropagation();
      event.preventDefault();
      this.#openModal(modalTrigger);
      return false;
    }

    const directAddButton = target?.closest('.quick-add__button--add');
    if (directAddButton && this.contains(directAddButton)) {
      event.stopImmediatePropagation();
      event.stopPropagation();
      event.preventDefault();
      void this.#submitDirectAddButton(directAddButton, event);
      return false;
    }

    const plus = target?.closest('.quick-add__button--plus');
    if (!plus) return;
    event.stopImmediatePropagation();
    event.stopPropagation();
    event.preventDefault();

    if (this.#usesModal()) {
      this.#openModal(plus);
      return false;
    }

    this.toggleSizeSelector(event);
    return false;
  };

  #onSizeClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const btn = target?.closest('.quick-add__size-button');
    if (!btn) return;
    event.stopImmediatePropagation();
    event.stopPropagation();
    event.preventDefault();
    this.#cartFromButton(btn);
    return false;
  };

  #onOutsideClick = (event) => {
    const panel = this.#sizePanel();
    if (!panel || panel.hasAttribute('hidden')) return;

    const plus = this.querySelector('.quick-add__button--plus');
    if (plus?.contains(event.target)) return;
    if (!this.contains(event.target)) this.#hidePanel();
  };

  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    const panel = this.#sizePanel();
    if (!panel || panel.hasAttribute('hidden')) return;

    event.stopPropagation();
    event.preventDefault();
    this.#hidePanel({ returnFocus: true });
  };

  #onVariantChange = (event) => {
    const link = this.querySelector('[data-ref="purchaseOptionsLink"]');
    if (!(link instanceof HTMLAnchorElement)) return;

    const responseCard = event.detail?.data?.html?.querySelector?.('product-card');
    const responseHref = responseCard instanceof HTMLElement ? responseCard.dataset.productCardUrl : '';
    if (responseHref) link.href = new URL(responseHref, window.location.origin).href;
  };

  #openModal(trigger = null, optionValueIds = null) {
    const dialog = document.getElementById('quick-add-dialog');
    if (!dialog || typeof dialog.openProduct !== 'function') return;

    const source = trigger instanceof HTMLElement ? trigger : null;
    const { productUrl } = this.#selectedCardOptionState(source);
    const ids = Array.isArray(optionValueIds) ? optionValueIds : this.#selectedOptionValueIds(source);
    this.#hidePanel();
    this.#clearError();

    dialog.openProduct({
      productUrl: productUrl || this.dataset.productUrl || '',
      productTitle: this.dataset.productTitle || '',
      optionValueIds: ids,
      trigger: source,
    });
  }

  toggleSizeSelector = (event) => {
    if (!event) return;
    event.preventDefault();
    event.stopImmediatePropagation();

    const panel = this.#sizePanel();
    if (!panel) return;
    const focusPanel = event instanceof KeyboardEvent || event.detail === 0;

    if (panel.hasAttribute('hidden')) {
      this.#showPanel({ focusPanel });
    } else {
      this.#hidePanel({ returnFocus: focusPanel });
    }
  };

  selectSize = async (event) => {
    if (!event) return;
    event.preventDefault?.();
    event.stopImmediatePropagation?.();
    if (event.target) await this.#cartFromButton(event.target);
  };

  selectOption = async (event) => {
    await this.selectSize(event);
  };

  #cartFromButton = async (button) => {
    const optionValue = button.dataset.optionValue || button.dataset.sizeValue;
    if (!optionValue) return;

    let info = null;
    try {
      info = await this.#resolveVariantFromButton(button);
    } catch {
      info = null;
    }
    if (!info?.available) {
      this.#openModal(button, this.#selectedOptionValueIds(button));
      return;
    }

    const { id: variantId } = info;
    const form = this.querySelector('form[data-type="add-to-cart-form"]');
    if (!form) return;

    const idInput = form.querySelector('input[name="id"]');
    if (idInput) idInput.value = String(variantId);

    // Mark selected option visually.
    const panel = this.#sizePanel();
    if (panel) {
      panel.querySelectorAll('.quick-add__size-button').forEach((b) => {
        b.classList.remove('quick-add__size-button--selected');
      });
      button.classList.add('quick-add__size-button--selected');
    }

    this.#hidePanel({ returnFocus: true });
    await this.#postToCart(form, String(variantId));
  };

  async #resolveVariantFromButton(button) {
    if (!(button instanceof HTMLElement)) return null;
    if (!this.#buttonCanResolve(button)) return null;

    const selectedOptionValueIds = this.#selectedOptionValueIds(button);
    const optionCount = this.#optionCount();

    if (optionCount === 1 && button.dataset.variantId) {
      return { id: button.dataset.variantId, available: button.dataset.optionAvailable !== 'false' };
    }

    const doc = await this.#fetchSelectedProductCard(button, selectedOptionValueIds);
    const variant = this.#extractSelectedVariant(doc);

    if (!variant?.id || variant.available === false) {
      button.classList.add('quick-add__size-button--unavailable', 'fit-quick-add__size-button--unavailable');
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('disabled', '');
      button.classList.remove('quick-add__size-button--selected');
      return null;
    }

    return { id: variant.id, available: true };
  }

  async #fetchSelectedProductCard(button, selectedOptionValueIds) {
    const { productUrl } = this.#selectedCardOptionState(button);
    if (!productUrl) return null;

    const url = preserveMarketContext(new URL(productUrl, window.location.origin));
    url.searchParams.set('section_id', 'section-rendering-product-card');
    if (selectedOptionValueIds.length) {
      url.searchParams.set('option_values', selectedOptionValueIds.join(','));
    }

    return sectionManager.getHTMLDocument(url, {
      cache: window.Shopify?.designMode !== true,
      signal: this.lifecycleSignal,
    });
  }

  #extractSelectedVariant(doc) {
    const script = doc?.querySelector?.(
      'product-card variant-select script[type="application/json"], product-card swatch-picker script[type="application/json"], variant-select script[type="application/json"], swatch-picker script[type="application/json"]',
    );
    if (!script?.textContent) return null;

    try {
      return JSON.parse(script.textContent);
    } catch {
      return null;
    }
  }

  async #submitDirectAddButton(button, event) {
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const form = button.closest('form[data-type="add-to-cart-form"]');
    if (!(form instanceof HTMLFormElement) || !this.contains(form)) return;

    const idInput = form.querySelector('input[name="id"]');
    const variantId = idInput?.value;
    if (!variantId) return;

    await this.#postToCart(
      form,
      variantId,
      cartPerformance.createStartingMarker('add:user-action', event),
      createCartAddOperationId('quick-add'),
    );
  }

  #onSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('form[data-type="add-to-cart-form"]')) return;

    event.preventDefault();
    event.stopPropagation();

    const idInput = form.querySelector('input[name="id"]');
    const variantId = idInput?.value;
    if (!variantId) return;

    await this.#postToCart(
      form,
      variantId,
      cartPerformance.createStartingMarker('add:user-action', event),
      createCartAddOperationId('quick-add'),
    );
  };

  async #postToCart(form, variantId, addUserActionMarker = null, cartAddOperationId = createCartAddOperationId('quick-add')) {
    if (this.classList.contains('quick-add--loading')) return;

    const formId = form.getAttribute('id') || '';
    const isCartDrawerUpsell = Boolean(form.closest('.cart-drawer-upsells'));
    const pendingEventData = cartPerformance.withUserActionMarker(
      {
        action: 'add',
        cartAddOperationId,
        source: 'quick-add',
        productId: this.dataset.productId || null,
        variantId,
      },
      addUserActionMarker,
    );
    let deferAddUserActionMetric = false;

    this.classList.add('quick-add--loading');
    this.setAttribute('aria-busy', 'true');
    this.#clearError();
    emitCartAddPending(this, formId, pendingEventData);

    try {
      const result = await submitCartAdd(form, {
        signal: this.lifecycleSignal,
        source: 'quick-add',
        productId: this.dataset.productId,
        quantityDefault: 1,
        variantId,
        cartAddOperationId,
        sectionIds: collectCartAddSectionIds(document, { sourceElement: form }),
        optimisticCount: !isCartDrawerUpsell,
      });
      if (result.aborted) {
        emitCartAddPendingClear(
          this,
          formId,
          cartPerformance.withUserActionMarker(result.eventData ?? { ...pendingEventData, aborted: true }, addUserActionMarker),
        );
        return;
      }

      if (!result.ok) {
        emitCartAddFailure(this, formId, result.error, cartPerformance.withUserActionMarker(result.eventData, addUserActionMarker));
        this.#showError(result.error);
        return;
      }

      this.#clearError();
      deferAddUserActionMetric = true;
      emitCartAddSuccess(this, formId, result.payload, cartPerformance.withUserActionMarker(result.eventData, addUserActionMarker));
    } catch (err) {
      const error = {
        message: 'Unable to add this item to your cart.',
        description: err instanceof Error ? err.message : 'Unknown error',
        errors: {},
      };

      emitCartAddFailure(
        this,
        formId,
        error,
        cartPerformance.withUserActionMarker(
          {
            action: 'add',
            source: 'quick-add',
            productId: this.dataset.productId || null,
            variantId,
          },
          addUserActionMarker,
        ),
      );
      this.#showError(error);
    } finally {
      this.classList.remove('quick-add--loading');
      this.removeAttribute('aria-busy');
      if (deferAddUserActionMetric) {
        cartPerformance.deferMeasureFromMarker(addUserActionMarker);
      } else {
        cartPerformance.measureFromMarker(addUserActionMarker);
      }
    }
  }

  #showError(error) {
    const message = this.#errorMessage(error);
    const alert = this.#errorElement();

    alert.textContent = message;
    alert.hidden = false;
    this.dataset.quickAddError = 'true';

    window.clearTimeout(this.#errorTimer);
    this.#errorTimer = window.setTimeout(() => {
      this.#clearError();
    }, QUICK_ADD_ERROR_DISPLAY_MS);
  }

  #clearError({ remove = false } = {}) {
    window.clearTimeout(this.#errorTimer);
    this.#errorTimer = null;

    const alert = this.querySelector('[data-quick-add-error]');
    if (!(alert instanceof HTMLElement)) return;

    if (remove) {
      alert.remove();
    } else {
      alert.textContent = '';
      alert.hidden = true;
    }

    delete this.dataset.quickAddError;
  }

  #errorElement() {
    const current = this.querySelector('[data-quick-add-error]');
    if (current instanceof HTMLElement) return current;

    const alert = document.createElement('div');
    alert.className = 'quick-add__error';
    alert.dataset.quickAddError = 'true';
    alert.hidden = true;
    alert.setAttribute('role', 'alert');
    alert.setAttribute('aria-live', 'assertive');
    this.append(alert);

    return alert;
  }

  #errorMessage(error) {
    const description = typeof error?.description === 'string' ? error.description.trim() : '';
    if (description) return description;

    const message = typeof error?.message === 'string' ? error.message.trim() : '';
    if (message) return message;

    if (error?.errors && typeof error.errors === 'object') {
      const first = Object.values(error.errors)
        .flat()
        .find((entry) => typeof entry === 'string' && entry.trim());
      if (first) return first.trim();
    }

    return window.FitCheck?.translations?.quick_add_cart_error || 'Unable to add this item to your cart.';
  }
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialogComponent);
}

if (!customElements.get('quick-add')) {
  customElements.define('quick-add', QuickAddComponent);
}
